<template>
  <v-container class="pt-10 pb-16 page-privacy">
    <v-row justify="center">
      <v-col cols="12" md="10">
        <h1 class="page-title mb-4">SERVICE PARTNER PRIVACY POLICY (USA)</h1>
        <div class="policy-meta mb-6">
          <p><strong>Operating Entity:</strong> OH YES LLC</p>
          <p><strong>Effective Date:</strong> January 21, 2026</p>
        </div>

        <section>
          <h3>1. INTRODUCTION</h3>
          <p>OH YES LLC ("we," "us," or "our") values the privacy of the independent professionals and businesses that use our platform (the "Service Partners"). This policy describes how we collect and process your personal and professional data to facilitate your connection with Customers.</p>
        </section>

        <section>
          <h3>2. DATA WE COLLECT FROM PARTNERS</h3>
          <p>To maintain a high-quality marketplace, we collect the following:</p>
          <ul>
            <li><strong>Professional Identifiers:</strong> Legal name, business name, business address, and Social Security Number (SSN) or Employer Identification Number (EIN) for tax reporting (Form 1099-NEC).</li>
            <li><strong>Verification Data:</strong> Photos of government-issued IDs, professional licenses (e.g., HVAC, Plumbing), and proof of insurance (COI).</li>
            <li><strong>Background Check Information:</strong> We use third-party providers to conduct criminal background checks and driving record reviews, as permitted by local and state laws.</li>
            <li><strong>Operational Location Data:</strong> We collect your precise geolocation while you are "Online" or "On the Way" to a job to provide real-time updates to the Customer and to verify job completion.</li>
            <li><strong>Financial Data:</strong> Bank account details for direct deposit payouts (processed via secure third-party processors like Stripe).</li>
          </ul>
        </section>

        <section>
          <h3>3. HOW WE USE PARTNER DATA</h3>
          <ul>
            <li><strong>Onboarding & Vetting:</strong> To verify your eligibility to perform professional services in your specific jurisdiction.</li>
            <li><strong>Job Matching:</strong> To alert you to service requests in your immediate vicinity.</li>
            <li><strong>Customer Transparency:</strong> We share your name, business name, profile photo, and real-time location with the Customer who has booked your service.</li>
            <li><strong>Tax Compliance:</strong> To generate and file annual 1099-NEC forms with the IRS.</li>
            <li><strong>Performance Monitoring:</strong> To track customer ratings and feedback to ensure platform quality.</li>
          </ul>
        </section>

        <section>
          <h3>4. SHARING OF INFORMATION</h3>
          <ul>
            <li><strong>With Customers:</strong> We share your professional profile and location once you accept a lead.</li>
            <li><strong>With Regulatory Bodies:</strong> We may share licensing or insurance data with local government agencies if required by law or during a safety audit.</li>
            <li><strong>With Service Providers:</strong> We share data with background check companies, cloud storage providers, and payment processors. We do not sell Partner data to third-party advertisers.</li>
          </ul>
        </section>

        <section>
          <h3>5. DATA RETENTION</h3>
          <ul>
            <li><strong>Tax Records:</strong> We are required by federal law to retain your tax-related information (income and EIN/SSN) for at least 7 years.</li>
            <li><strong>Background Checks:</strong> We retain background check results for the duration of your active partnership plus a period of 2 years for legal defense purposes.</li>
            <li><strong>Account Deletion:</strong> If you terminate your partnership, we will de-identify your geolocation data, but we must retain contract and transaction history for legal compliance.</li>
          </ul>
        </section>

        <section>
          <h3>6. YOUR RIGHTS (USA/CCPA)</h3>
          <p>Under the CCPA/CPRA, as a Service Partner in the USA, you have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of the data we hold about your business.</li>
            <li><strong>Correction:</strong> Update your professional licenses or contact info at any time via the Partner App.</li>
            <li><strong>Opt-Out of Profiling:</strong> You can opt-out of automated decision-making regarding job leads, though this may limit your ability to receive new service requests.</li>
          </ul>
        </section>

        <section>
          <h3>7. SECURITY</h3>
          <p>We use administrative, technical, and physical security measures to protect your professional data. Access to SSNs and bank details is strictly restricted to our finance and compliance teams.</p>
        </section>

        <section>
          <h3>8. CONTACT US</h3>
          <p>For privacy inquiries or to exercise your data rights, please contact the OH YES LLC Compliance Officer at:</p>
          <ul>
            <li><strong>Email:</strong> [Your Support Email]</li>
            <li><strong>Address:</strong> [Your Business Address in IL]</li>
          </ul>
        </section>

        <section>
          <h3>Implementation for OH YES LLC (USA)</h3>
          <ol>
            <li><strong>Mandatory W-9:</strong> Your Partner App should include a digital W-9 collection screen. This is critical for 1099 compliance in the US.</li>
            <li><strong>Location Disclosure:</strong> You must have a clear "Background Location" disclosure in the app store listing. Apple and Google are very strict about apps that track location while the screen is off.</li>
            <li><strong>Third-Party Vetting:</strong> Ensure your background check provider (e.g., Checkr or Sterling) is mentioned as a "Service Provider" in your internal disclosures.</li>
          </ol>
        </section>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: "PartnerPrivacyUsa",
};
</script>

<style scoped>
.page-title { font-size: 34px; font-weight: 700; }
.policy-meta p { font-size: 15px; color: #444; line-height: 1.7; margin-bottom: 4px; }
section { margin-bottom: 28px; }
h3 { font-size: 16px; font-weight: 600; margin-top: 18px; margin-bottom: 6px; }
p, li { font-size: 15px; color: #444; line-height: 1.7; }
ul, ol { padding-left: 20px; }
ol { list-style-type: decimal; }
</style>
